import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faPhone, faHospital, faBriefcase, faPen, faSpinner, faCamera } from '@fortawesome/free-solid-svg-icons';
import API_URL from '../config';

const ProfileSection = ({ icon, label, fieldKey, value, type = 'text', fullWidth = false, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');

  useEffect(() => {
      setEditValue(value || '');
  }, [value]);

  const handleSave = () => {
    setIsEditing(false);
    if (editValue !== value) {
      onUpdate(fieldKey, editValue);
    }
  };

  return (
    <div className={`flex items-center p-3 sm:p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 relative group transition-all duration-200 ${isEditing ? 'ring-1 ring-blue-500 border-blue-500' : 'hover:border-gray-200 dark:hover:border-gray-600'} ${fullWidth ? 'w-full' : ''}`}>
      <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-gray-50 dark:bg-gray-700 rounded-lg shrink-0 transition-colors">
        <FontAwesomeIcon icon={icon} className={`${isEditing ? 'text-blue-500' : 'text-gray-500 dark:text-gray-400'} text-lg`} />
      </div>
      <div className="ml-4 flex-grow min-w-0 pr-8">
        <p className="text-xs uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 mb-0.5">{label}</p>
        {isEditing ? (
          <input
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            autoFocus
            className="block w-full text-base sm:text-sm font-medium bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 p-0 border-b border-gray-300 dark:border-gray-600"
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        ) : (
          <p className="text-base sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {value || <span className="text-gray-400 italic font-normal">Not provided</span>}
          </p>
        )}
      </div>
      {!isEditing && (
        <button 
          onClick={() => setIsEditing(true)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 md:opacity-0 group-hover:opacity-100 transition-all duration-200 p-2 rounded-full hover:bg-blue-50 dark:hover:bg-gray-700 focus:outline-none"
          aria-label={`Edit ${label}`}
        >
          <FontAwesomeIcon icon={faPen} />
        </button>
      )}
    </div>
  );
};

const Profile = () => {
  const [userProfile, setUserProfile] = useState({
    name: '',
    email: '',
    phone: '',
    institution: '',
    role: '',
    joinDate: '',
    profile_image: null,
  });
  
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile/`);
      if (response.ok) {
        const data = await response.json();
        setUserProfile(data);
      }
    } catch (e) {
      console.error("Failed to fetch profile", e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (field, value) => {
    try {
      const formData = new FormData();
      formData.append(field, value);
      
      const response = await fetch(`${API_URL}/api/profile/`, {
        method: 'PATCH',
        body: formData, // native fetch with FormData handles multipart boundaries automatically
      });
      
      if (response.ok) {
        const updatedData = await response.json();
        setUserProfile(updatedData);
      }
    } catch (e) {
      console.error("Failed to update profile", e);
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleUpdate('profile_image', e.target.files[0]);
    }
  };

  if (loading) {
     return (
       <div className="flex h-[calc(100vh-200px)] items-center justify-center">
         <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-4xl" />
       </div>
     );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Profile Settings</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Manage your personal information and application preferences.</p>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header Section with Avatar */}
        <div className="px-6 py-8 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div 
              className="relative w-28 h-28 sm:w-32 sm:h-32 bg-white dark:bg-gray-700 rounded-full flex items-center justify-center border-4 border-white dark:border-gray-800 shadow-md cursor-pointer overflow-hidden group shrink-0"
              onClick={() => fileInputRef.current?.click()}
            >
              {userProfile.profile_image ? (
                <img 
                  src={`${API_URL}${userProfile.profile_image}`} 
                  alt="Profile" 
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
                />
              ) : (
                <FontAwesomeIcon icon={faUser} className="text-gray-300 dark:text-gray-500 text-5xl" />
              )}
              
              <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <FontAwesomeIcon icon={faCamera} className="text-white text-xl mb-1" />
                <span className="text-white text-xs font-medium">Change</span>
              </div>
            </div>
            
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleImageChange} 
              className="hidden" 
              accept="image/*" 
            />
            
            <div className="flex-grow w-full space-y-3">
               <ProfileSection onUpdate={handleUpdate} icon={faUser} label="Full Name" fieldKey="name" value={userProfile.name} fullWidth />
               <ProfileSection onUpdate={handleUpdate} icon={faBriefcase} label="Professional Role" fieldKey="role" value={userProfile.role} fullWidth />
            </div>
          </div>
        </div>

        {/* Detailed Info Section */}
        <div className="p-6">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ProfileSection onUpdate={handleUpdate} icon={faEnvelope} label="Email Address" fieldKey="email" value={userProfile.email} type="email" />
            <ProfileSection onUpdate={handleUpdate} icon={faPhone} label="Phone Number" fieldKey="phone" value={userProfile.phone} type="tel" />
            <ProfileSection onUpdate={handleUpdate} icon={faHospital} label="Institution" fieldKey="institution" value={userProfile.institution} />
          </div>
        </div>

        {/* Footer/Account Info */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/80 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Account status</span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
              Active Member since {userProfile.joinDate}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;